import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  Grid,
  Card,
  CardMedia,
  CardActions,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { FaultTopic, FaultCategory } from '../../types/maintenanceBank';
import { FaultTopicService, FaultCategoryService } from '../../services/maintenanceBankService';
import { ImageUploadService } from '../../services/imageUploadService';
import WYSIWYGEditor from '../../components/WYSIWYGEditor';

const TopicEditPage: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  
  const [topic, setTopic] = useState<FaultTopic | null>(null);
  const [categories, setCategories] = useState<FaultCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // بيانات النموذج
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [content, setContent] = useState('');
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [estimatedTime, setEstimatedTime] = useState(30);
  const [tags, setTags] = useState('');
  const [isPublished, setIsPublished] = useState(true);

  // تحميل البيانات
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // تحميل الفئات
      const categoriesData = await FaultCategoryService.getCategories();
      setCategories(categoriesData);
      
      // تحميل الموضوع إذا كان للتعديل
      if (topicId) {
        console.log('🔄 تحميل الموضوع للتعديل:', topicId);
        const topicData = await FaultTopicService.getTopic(topicId);
        
        if (!topicData) {
          setError('الموضوع غير موجود');
          return;
        }
        
        setTopic(topicData);
        
        // ملء النموذج بالبيانات
        setTitle(topicData.title);
        setDescription(topicData.description || '');
        setCategoryId(topicData.categoryId);
        setContent(topicData.content);
        setDifficulty(topicData.difficulty);
        setEstimatedTime(topicData.estimatedTime || 30);
        setTags(Array.isArray(topicData.tags) ? topicData.tags.join(', ') : '');
        setIsPublished(topicData.isPublished);
        
        console.log('✅ تم تحميل الموضوع للتعديل');
      }
    } catch (error: any) {
      console.error('❌ خطأ في تحميل البيانات:', error);
      setError(`فشل في تحميل البيانات: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [topicId]);

  // العودة للخلف
  const handleBack = () => {
    if (topicId) {
      navigate(`/admin/maintenance-bank/topic/${topicId}`);
    } else {
      navigate('/admin/maintenance-bank');
    }
  };

  // حفظ التغييرات
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // التحقق من البيانات المطلوبة
      if (!title.trim()) {
        setError('عنوان الموضوع مطلوب');
        return;
      }

      if (!categoryId) {
        setError('يجب اختيار فئة');
        return;
      }

      if (!content.trim()) {
        setError('محتوى الموضوع مطلوب');
        return;
      }

      // تحضير العلامات
      const tagsArray = tags.trim() ? tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0) : [];

      const topicData: any = {
        title: title.trim(),
        description: description.trim(),
        categoryId,
        content: content.trim(),
        difficulty,
        estimatedTime,
        tags: tagsArray,
        isPublished
      };

      // إضافة الحقول المطلوبة للمواضيع الجديدة فقط
      if (!topicId) {
        topicData.order = 0;
        topicData.images = [];
        topicData.attachments = [];
        topicData.createdBy = 'current-user'; // يجب تحديثه بمعرف المستخدم الحالي
      }

      console.log('💾 حفظ الموضوع:', topicData);

      if (topicId) {
        // تحديث موضوع موجود
        await FaultTopicService.updateTopic(topicId, topicData);
        console.log('✅ تم تحديث الموضوع بنجاح');
        
        // العودة لصفحة عرض الموضوع
        navigate(`/admin/maintenance-bank/topic/${topicId}`);
      } else {
        // إنشاء موضوع جديد
        const newTopicId = await FaultTopicService.addTopic(topicData);
        console.log('✅ تم إنشاء الموضوع بنجاح:', newTopicId);
        
        // الانتقال لصفحة عرض الموضوع الجديد
        navigate(`/admin/maintenance-bank/topic/${newTopicId}`);
      }
    } catch (error: any) {
      console.error('❌ خطأ في حفظ الموضوع:', error);
      setError(`فشل في حفظ الموضوع: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* شريط التنقل العلوي */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            {topicId ? 'تعديل الموضوع' : 'إضافة موضوع جديد'}
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'جاري الحفظ...' : 'حفظ'}
        </Button>
      </Box>

      {/* رسالة الخطأ */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* نموذج التعديل */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* العمود الأيسر - المعلومات الأساسية */}
          <Grid item xs={12} md={6}>
            <Box display="flex" flexDirection="column" gap={3}>
              {/* عنوان الموضوع */}
              <TextField
                label="عنوان الموضوع"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                variant="outlined"
              />

              {/* وصف مختصر */}
              <TextField
                label="وصف مختصر"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                helperText="وصف مختصر يظهر في قائمة المواضيع"
              />

              {/* الفئة */}
              <FormControl fullWidth required>
                <InputLabel>الفئة</InputLabel>
                <Select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  label="الفئة"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.icon} {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* مستوى الصعوبة */}
              <FormControl fullWidth>
                <InputLabel>مستوى الصعوبة</InputLabel>
                <Select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value as 'easy' | 'medium' | 'hard')}
                  label="مستوى الصعوبة"
                >
                  <MenuItem value="easy">🟢 سهل</MenuItem>
                  <MenuItem value="medium">🟡 متوسط</MenuItem>
                  <MenuItem value="hard">🔴 صعب</MenuItem>
                </Select>
              </FormControl>

              {/* الوقت المقدر */}
              <Box>
                <Typography gutterBottom>
                  الوقت المقدر للإصلاح: {estimatedTime} دقيقة
                </Typography>
                <Slider
                  value={estimatedTime}
                  onChange={(_, value) => setEstimatedTime(value as number)}
                  min={5}
                  max={180}
                  step={5}
                  marks={[
                    { value: 5, label: '5 د' },
                    { value: 30, label: '30 د' },
                    { value: 60, label: '1 س' },
                    { value: 120, label: '2 س' },
                    { value: 180, label: '3 س' }
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>

              {/* العلامات */}
              <TextField
                label="العلامات"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                fullWidth
                variant="outlined"
                helperText="افصل العلامات بفاصلة (مثال: شاشة, كسر, استبدال)"
              />

              {/* حالة النشر */}
              <FormControlLabel
                control={
                  <Switch
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                  />
                }
                label="نشر الموضوع فوراً"
              />
            </Box>
          </Grid>

          {/* العمود الأيمن - المحرر */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h6" gutterBottom>
                محتوى الموضوع:
              </Typography>
              
              <WYSIWYGEditor
                value={content}
                onChange={setContent}
                placeholder="اكتب محتوى الموضوع هنا..."
                minHeight={400}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default TopicEditPage;
